import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, computed, effect, inject, untracked, viewChild } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { GridGrouping, GridModel } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { ItemComponent } from '@frontend/sports/item/feature';
import { LoadingIndicatorModule } from '@frontend/sports/loading-indicator-feature';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { FavouritesGridPayload } from '@frontend/sports/types/components/widget/types';
import { PrimaryNavigationDisplayType } from '@frontend/sports/types/configuration';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { WidgetHeaderComponent } from '@frontend/sports/widget/feature/header';
import { WidgetPillComponent } from '@frontend/sports/widget/feature/pills';
import { WidgetTabBarComponent, WidgetTabBarItem } from '@frontend/sports/widget/feature/tab-bar';
import { DeviceService } from '@frontend/vanilla/core';

import { ErrorComponent } from '../common/error/error.component';
import { GridComponent } from '../grid/grid.component';
import { GridModule } from '../grid/grid.module';
import { FAVOURITES_GRID_WIDGET_TRACKING_CONSTS, FavouritesGridTrackingService } from './favourites-grid-tracking.service';
import { FavouritesGridWidgetService } from './favourites-grid-widget.service';
import { FavouritesGridNavigation } from './favourites-grid.model';

@Component({
    imports: [
        AsyncPipe,
        LoadingIndicatorModule,
        GridModule,
        ErrorComponent,
        ItemComponent,
        TrustPipe,
        WidgetPillComponent,
        WidgetTabBarComponent,
        WidgetHeaderComponent,
    ],
    selector: 'ms-favourites-grid-widget',
    templateUrl: './favourites-grid-widget.component.html',
    host: {
        class: 'tabbed-grid-widget grid-wrapper',
    },
    providers: [FavouritesGridTrackingService],
})
export class FavouritesGridWidgetComponent extends WidgetComponent<FavouritesGridPayload> {
    private readonly gridComponent = viewChild(GridComponent);

    private readonly gridConfig = inject(GridConfig);
    private readonly urlHelper = inject(UrlHelperService);
    private readonly gridFactory = inject(ObservableGridFactory);
    private readonly trackingService = inject(FavouritesGridTrackingService);
    private readonly favouritesGridWidgetService = inject(FavouritesGridWidgetService);
    private readonly changeRef = inject(ChangeDetectorRef);
    private readonly deviceService = inject(DeviceService);
    private readonly sitecore = inject(Sitecore);

    protected title: string;
    protected footerLink?: string;
    protected footerLinkTitle: string;
    protected isFooterVisible?: boolean;
    protected tracking: string;
    protected grid$: ObservableGrid;

    protected hideGridTitle = this.deviceService.isMobile;
    protected showPrimaryNavAsPill = this.gridConfig.primaryNavigationType === PrimaryNavigationDisplayType.Pill;
    protected contentErrorMessage = this.sitecore.event.ErrNoEventsPerSport;

    protected navigation = computed(() => this.favouritesGridWidgetService.widgetNavigation());
    protected activeFixturesMapperEffect = effect(() => {
        const activeFixtures = this.favouritesGridWidgetService.widgetActiveFixtures();
        // Wait until the active fixtures are loaded after click but only if they are not in cache already.
        if (activeFixtures.length > 0) {
            const activeTab = this.favouritesGridWidgetService.widgetActiveTab();
            untracked(() => {
                this.mapWidgetContent(activeTab, activeFixtures);
            });
        }
    });

    protected gridActivationTracking = effect(() => {
        const gridComponent = this.gridComponent();
        if (gridComponent) {
            this.trackVisibility(gridComponent.model, this.config.order);
        }
    });

    constructor() {
        super();
        inject(DestroyRef).onDestroy(() => {
            this.grid$?.destroy();
            this.favouritesGridWidgetService.cleanUpWidgetCache();
        });
    }

    protected override onData(data: FavouritesGridPayload): void {
        this.mapWidgetConfig(data);
        this.mapWidgetContent(this.favouritesGridWidgetService.widgetActiveTab(), this.favouritesGridWidgetService.widgetActiveFixtures());
    }

    navigationSelected(tabEvent: WidgetTabBarItem): void {
        this.favouritesGridWidgetService.loadActiveSport(tabEvent.itemId.toString());
    }

    footerLinkClick(): void {
        this.trackingService.trackLinkClick({
            eventDetails: FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.EVENT_DETAIL,
            urlClicked: this.footerLink,
        });
    }

    eventClicked({ event, index }: { event: EventModel; index: number }): void {
        this.trackingService.trackEventClick(event, index);
    }

    private trackVisibility(grid: GridModel, order: number): void {
        this.trackingService.trackWidgetVisible(grid, order);
    }

    private mapWidgetConfig(payload: FavouritesGridPayload) {
        this.title = payload.title ?? '';

        this.isFooterVisible = payload.bottomLink?.visible;
        if (this.isFooterVisible) {
            this.footerLinkTitle = payload.bottomLink?.title ?? '';
            this.footerLink = this.urlHelper.getFavouriteUrl();
        }
    }

    private mapWidgetContent(activeTab: FavouritesGridNavigation | undefined, fixtures: Fixture[]) {
        this.grid$?.destroy();

        this.grid$ = this.gridFactory.fromResponse(`${this.config.id}/${activeTab?.itemId ?? ''}`, fixtures, {
            grouping: GridGrouping.None,
            disableGroupSorting: false,
        });

        this.setTracking(activeTab);
        //Needs to speed up tab switching otherwise it would take too much time (2-3 secs delay) until the template picks up the changes
        this.changeRef.markForCheck();
    }

    private setTracking(activeTab: FavouritesGridNavigation | undefined): void {
        if (activeTab) {
            this.trackingService.trackTabSelected(activeTab);
            this.tracking = this.trackingService.getTrackingValueForMatchList(activeTab);
        } else {
            this.tracking = FAVOURITES_GRID_WIDGET_TRACKING_CONSTS.FALL_BACK_SPORT;
        }
    }
}
